import React, { useEffect, useState } from "react";
import { User, Save, Link2, Copy, RefreshCw, Eye, EyeOff, ExternalLink } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const ROLES = ["Batter", "Bowler", "All-Rounder", "Wicket Keeper", "Wicket Keeper-Batter"];
const BATTING_STYLES = ["Right-Hand Bat", "Left-Hand Bat"];
const BOWLING_STYLES = ["Fast", "Fast-Medium", "Medium", "Off-Spin", "Leg-Spin", "Left-Arm Spin", "Left-Arm Pace", "None"];

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const p = user?.profile || {};
  const [form, setForm] = useState({
    name: p.name || user?.name || "",
    age: p.age || 21,
    playing_role: p.playing_role || "All-Rounder",
    batting_style: p.batting_style || "Right-Hand Bat",
    bowling_style: p.bowling_style || "Fast-Medium",
    team: p.team || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [shareToken, setShareToken] = useState(null);
  const [shareBusy, setShareBusy] = useState(false);

  useEffect(() => {
    // Fetch fresh me to read share_token
    async function loadShare() {
      try {
        const { data } = await api.get("/auth/me");
        if (data?.share_token) setShareToken(data.share_token);
      } catch (_) {}
    }
    loadShare();
  }, []);

  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.put("/profile", { ...form, age: Number(form.age) });
      await refreshUser();
      toast.success("Profile updated");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const enableShare = async () => {
    setShareBusy(true);
    try {
      const { data } = await api.post("/share/enable");
      setShareToken(data.share_token);
      toast.success("Public share link enabled");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setShareBusy(false);
    }
  };

  const disableShare = async () => {
    if (!window.confirm("Disable public share link? Anyone with the old link will lose access.")) return;
    setShareBusy(true);
    try {
      await api.post("/share/disable");
      setShareToken(null);
      toast.success("Share link disabled");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setShareBusy(false);
    }
  };

  const regenerateShare = async () => {
    if (!window.confirm("Regenerate share code? The old link will stop working immediately.")) return;
    setShareBusy(true);
    try {
      const { data } = await api.post("/share/regenerate");
      setShareToken(data.share_token);
      toast.success("New share code generated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setShareBusy(false);
    }
  };

  const shareUrl = shareToken ? `${window.location.origin}/share/${shareToken}` : "";
  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed. Please copy manually.");
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn max-w-3xl mx-auto">
      <div className="bg-[#141414] border border-[#262626] p-6 rounded-xl flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-[#FF3B30] flex items-center justify-center text-white font-black text-lg uppercase">
          {(form.name || "P").slice(0, 2)}
        </div>
        <div>
          <h3 className="text-2xl font-black uppercase text-white">Player Profile</h3>
          <p className="text-xs text-[#A3A3A3]">Signed in as {user?.email}</p>
        </div>
      </div>

      <form onSubmit={submit} className="bg-[#141414] border border-[#262626] rounded-xl p-6 space-y-5">
        <Field label="Full Name">
          <input data-testid="profile-name" required value={form.name} onChange={(e) => upd("name", e.target.value)} className={cls} />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Age">
            <input data-testid="profile-age" type="number" min="8" max="80" required value={form.age} onChange={(e) => upd("age", e.target.value)} className={cls} />
          </Field>
          <Field label="Team">
            <input data-testid="profile-team" value={form.team} onChange={(e) => upd("team", e.target.value)} className={cls} placeholder="e.g. State U-23" />
          </Field>
          <Field label="Playing Role">
            <select data-testid="profile-role" value={form.playing_role} onChange={(e) => upd("playing_role", e.target.value)} className={cls}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Batting Style">
            <select data-testid="profile-batting" value={form.batting_style} onChange={(e) => upd("batting_style", e.target.value)} className={cls}>
              {BATTING_STYLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Bowling Style">
            <select data-testid="profile-bowling" value={form.bowling_style} onChange={(e) => upd("bowling_style", e.target.value)} className={cls}>
              {BOWLING_STYLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
        </div>

        {error && (
          <div data-testid="profile-error" className="bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-[#FF3B30] text-xs rounded px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end pt-2">
          <button
            data-testid="profile-save-btn"
            type="submit"
            disabled={busy}
            className="bg-[#FF3B30] hover:bg-[#E03229] disabled:opacity-50 text-white font-bold px-6 py-2.5 rounded text-sm flex items-center gap-2 shadow-lg"
          >
            <Save className="w-4 h-4" /> {busy ? "Saving..." : "Save Profile"}
          </button>
        </div>
      </form>

      {/* PUBLIC SHARE LINK */}
      <div className="bg-[#141414] border border-[#262626] rounded-xl p-6 space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded bg-[#FF3B30]/20 border border-[#FF3B30]/40 flex items-center justify-center text-[#FF3B30]">
              <Link2 className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-lg font-bold uppercase text-white">Public Share Link</h4>
              <p className="text-xs text-[#A3A3A3]">Send a read-only version of your stats & AI report to scouts.</p>
            </div>
          </div>
          {shareToken ? (
            <span className="bg-emerald-500/20 text-emerald-400 text-[10px] font-mono font-bold px-2.5 py-1 rounded border border-emerald-500/40 uppercase flex items-center gap-1">
              <Eye className="w-3 h-3" /> Active
            </span>
          ) : (
            <span className="bg-[#262626] text-[#A3A3A3] text-[10px] font-mono font-bold px-2.5 py-1 rounded border border-[#333] uppercase flex items-center gap-1">
              <EyeOff className="w-3 h-3" /> Disabled
            </span>
          )}
        </div>

        {shareToken ? (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-mono text-[#A3A3A3] uppercase mb-1">Share URL (send to scouts)</label>
              <div className="flex gap-2">
                <input
                  data-testid="share-url-input"
                  readOnly
                  value={shareUrl}
                  className={`${cls} font-mono text-xs`}
                  onFocus={(e) => e.target.select()}
                />
                <button
                  data-testid="share-url-copy"
                  onClick={() => copy(shareUrl, "Share URL")}
                  type="button"
                  className="bg-[#1A1A1A] hover:bg-[#262626] border border-[#262626] text-white px-3 rounded flex items-center gap-1.5 text-xs font-bold"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
                <a
                  data-testid="share-url-open"
                  href={shareUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="bg-[#1A1A1A] hover:bg-[#262626] border border-[#262626] text-white px-3 rounded flex items-center gap-1.5 text-xs font-bold"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open
                </a>
              </div>
            </div>
            <div>
              <label className="block text-xs font-mono text-[#A3A3A3] uppercase mb-1">Share Code (for Head-to-Head Compare)</label>
              <div className="flex gap-2">
                <input
                  data-testid="share-code-input"
                  readOnly
                  value={shareToken}
                  className={`${cls} font-mono text-xs`}
                  onFocus={(e) => e.target.select()}
                />
                <button
                  data-testid="share-code-copy"
                  onClick={() => copy(shareToken, "Share code")}
                  type="button"
                  className="bg-[#1A1A1A] hover:bg-[#262626] border border-[#262626] text-white px-3 rounded flex items-center gap-1.5 text-xs font-bold"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
              </div>
            </div>
            <div className="flex gap-2 pt-2 border-t border-[#262626]">
              <button
                data-testid="share-regenerate-btn"
                type="button"
                onClick={regenerateShare}
                disabled={shareBusy}
                className="bg-[#1A1A1A] hover:bg-[#262626] disabled:opacity-50 border border-[#262626] text-white px-4 py-2 rounded text-xs font-bold flex items-center gap-1.5"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Regenerate Code
              </button>
              <button
                data-testid="share-disable-btn"
                type="button"
                onClick={disableShare}
                disabled={shareBusy}
                className="bg-[#FF3B30]/10 hover:bg-[#FF3B30]/20 disabled:opacity-50 border border-[#FF3B30]/40 text-[#FF3B30] px-4 py-2 rounded text-xs font-bold flex items-center gap-1.5"
              >
                <EyeOff className="w-3.5 h-3.5" /> Disable Share
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-[#A3A3A3] mb-4">
              Turn this on to get a shareable read-only URL and a compare code.
            </p>
            <button
              data-testid="share-enable-btn"
              type="button"
              onClick={enableShare}
              disabled={shareBusy}
              className="bg-[#FF3B30] hover:bg-[#E03229] disabled:opacity-50 text-white font-bold px-6 py-2.5 rounded text-sm flex items-center gap-2 mx-auto shadow-lg"
            >
              <Link2 className="w-4 h-4" /> {shareBusy ? "Enabling..." : "Enable Public Share"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const cls = "w-full bg-[#1A1A1A] border border-[#262626] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF3B30]";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-mono text-[#A3A3A3] uppercase mb-1">{label}</label>
      {children}
    </div>
  );
}
